// Push Notification Service
// Handles Web Push subscription and permission management

import { config } from '../config'

const VAPID_PUBLIC_KEY = config.vapidPublicKey

// Convert VAPID key to Uint8Array for subscription
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray.buffer
}

export type NotificationPermission = 'default' | 'granted' | 'denied'

export interface PushSubscriptionData {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null
  private subscription: PushSubscription | null = null

  // Check if push notifications are supported
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window
  }

  // Get current permission status
  getPermission(): NotificationPermission {
    if (!this.isSupported()) return 'denied'
    return Notification.permission as NotificationPermission
  }

  // Request notification permission
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      console.warn('Push notifications not supported')
      return 'denied'
    }

    const permission = await Notification.requestPermission()
    return permission as NotificationPermission
  }

  // Initialize service worker registration
  async init(userId?: string): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('Push notifications not supported')
      return false
    }

    try {
      // Wait for service worker to be ready
      this.registration = await navigator.serviceWorker.ready
      console.log('[Push] Service worker ready')

      // Check for existing subscription
      this.subscription = await this.registration.pushManager.getSubscription()
      if (this.subscription) {
        console.log('[Push] Existing subscription found')
        // Re-save to server to ensure it's linked to current user
        const subscriptionData = this.subscription.toJSON() as PushSubscriptionData
        try {
          await this.saveSubscription(subscriptionData, userId)
          console.log('[Push] Existing subscription synced to server')
        } catch (e) {
          console.warn('[Push] Failed to sync existing subscription:', e)
        }
      }

      return true
    } catch (error) {
      console.error('[Push] Init failed:', error)
      return false
    }
  }

  // Subscribe to push notifications
  async subscribe(userId?: string): Promise<PushSubscriptionData | null> {
    if (!this.registration) {
      const initialized = await this.init()
      if (!initialized) return null
    }

    // Request permission first
    const permission = await this.requestPermission()
    if (permission !== 'granted') {
      console.warn('[Push] Permission not granted')
      return null
    }

    if (!VAPID_PUBLIC_KEY) {
      console.error('[Push] VAPID public key not configured')
      return null
    }

    try {
      // Subscribe to push manager
      this.subscription = await this.registration!.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      })

      const subscriptionData = this.subscription.toJSON() as PushSubscriptionData

      // Send subscription to server
      await this.saveSubscription(subscriptionData, userId)

      console.log('[Push] Subscribed successfully')
      return subscriptionData
    } catch (error) {
      console.error('[Push] Subscribe failed:', error)
      return null
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(): Promise<boolean> {
    if (!this.subscription) {
      console.warn('[Push] No subscription to unsubscribe')
      return true
    }

    try {
      // Remove from server first
      await this.removeSubscription(this.subscription.endpoint)

      // Then unsubscribe locally
      await this.subscription.unsubscribe()
      this.subscription = null

      console.log('[Push] Unsubscribed successfully')
      return true
    } catch (error) {
      console.error('[Push] Unsubscribe failed:', error)
      return false
    }
  }

  // Check if currently subscribed
  isSubscribed(): boolean {
    return this.subscription !== null
  }

  // Get current subscription
  getSubscription(): PushSubscription | null {
    return this.subscription
  }

  // Save subscription to server
  private async saveSubscription(subscription: PushSubscriptionData, userId?: string): Promise<void> {
    const API_URL = config.apiUrl

    const response = await fetch(`${API_URL}/api/push-subscriptions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        subscription,
        user_id: userId
      })
    })

    if (!response.ok) {
      throw new Error('Failed to save subscription')
    }
  }

  // Remove subscription from server
  private async removeSubscription(endpoint: string): Promise<void> {
    const API_URL = config.apiUrl

    const response = await fetch(`${API_URL}/api/push-subscriptions`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ endpoint })
    })

    if (!response.ok) {
      throw new Error('Failed to remove subscription')
    }
  }

  // Show a local notification (for testing)
  async showLocalNotification(title: string, options?: NotificationOptions): Promise<void> {
    if (!this.registration) {
      await this.init()
    }

    if (Notification.permission !== 'granted') {
      console.warn('[Push] Cannot show notification - permission not granted')
      return
    }

    await this.registration?.showNotification(title, {
    icon: '/pwa-192x192.png',
    badge: '/pwa-192x192.png',
      ...options
    })
  }
}

export const pushNotifications = new PushNotificationService()

